import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type VetMeResponse, vetApi } from './vet_api';

interface State {
  status: 'loading' | 'ok' | 'unauthenticated' | 'error';
  me: VetMeResponse | null;
  error: string | null;
}

/**
 * Gate that fetches /api/vet/me. Redirects to /sign-in on 401. Renders
 * children when authenticated.
 */
export function VetAuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading', me: null, error: null });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await vetApi<VetMeResponse>('/api/vet/me');
        if (!cancelled) setState({ status: 'ok', me, error: null });
      } catch (err) {
        const e = err as { status?: number };
        if (e.status === 401) {
          if (!cancelled) setState({ status: 'unauthenticated', me: null, error: null });
          navigate('/sign-in', { replace: true });
          return;
        }
        if (!cancelled) {
          setState({ status: 'error', me: null, error: 'Could not load your profile.' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center text-zinc-500">Loading…</div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center text-rose-600">
        {state.error}
      </div>
    );
  }
  if (state.status === 'unauthenticated') return null;
  return <>{children}</>;
}
